import {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  InputHTMLAttributes,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { SVGName } from "../../../assets/svgs/GenericSVG";

import { Body, H4, Mini } from "../../styles/stringStyles";
import { useStyles } from "../../styles/theme";
import { hexWithOpacity } from "../../utils/hexWithOpacity";
import { IconButton } from "../buttons/IconButton";
import { SkeletonPlaceholder } from "../loading/SkeletonPlaceholder";

type Props = {
  error?: string;
  value?: string;
  onChange?: (_value: string) => void;
  placeholder?: string;
  onBlur?: (value: string) => void;
  onFocus?: () => void;
  style?: CSSProperties;
  disabled?: boolean;
  type?: InputHTMLAttributes<HTMLInputElement>["type"];
  autoComplete?: InputHTMLAttributes<HTMLInputElement>["autoComplete"];
  onSubmit?: InputHTMLAttributes<HTMLInputElement>["onSubmit"];
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
  id?: string;
  label?: string;
  loadingData?: boolean;
  noPasswordManager?: boolean;
  withoutError?: boolean;
  icon?: SVGName;
  hasDeleteIcon?: boolean;
  className?: string;
  tag?: string;
};

export const TextInput = forwardRef(
  (props: Props, ref: ForwardedRef<HTMLInputElement>) => {
    const theme = useStyles();
    const [textHidden, setTextHidden] = useState(true);
    const inputRef = useRef<HTMLInputElement>(null);
    const isIconClick = useRef(false);

    const handleBlur = (event: React.FocusEvent) => {
      // If it's an icon click, prevent blur and maintain focus
      if (isIconClick.current) {
        event.preventDefault();
        // Reset the flag
        isIconClick.current = false;
        // Re-focus the input
        const input =
          (ref as React.RefObject<HTMLInputElement>)?.current ||
          inputRef.current;
        if (input) {
          input.focus();
        }
        return;
      }
      props.onBlur?.((event.target as HTMLInputElement).value);
    };

    const handleFocus = () => {
      props.onFocus?.();
    };

    const handleIconClick = (event?: React.MouseEvent) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      // Set the flag to indicate this is an icon click
      isIconClick.current = true;

      // Focus the input
      const input =
        (ref as React.RefObject<HTMLInputElement>)?.current || inputRef.current;
      if (input) {
        input.focus();
      }
    };

    const handleDeleteIconClick = (event?: React.MouseEvent) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      props.onChange?.("");
    };

    const handleKey = (e) => {
      if (e?.key == "Enter") {
        e.target.blur();
      }
    };

    return (
      <Container style={props.style} className={props.className}>
        <LabelContainer>
          {props.label && <StyledMini>{props.label}</StyledMini>}
          {props.tag && <TagValue>{props.tag}</TagValue>}
        </LabelContainer>
        {props.loadingData ? (
          <StyledPlaceholder />
        ) : (
          <InputWrapper>
            <StyledInput
              id={props.id}
              disabled={props.disabled}
              ref={ref || inputRef}
              type={
                props.type === "password"
                  ? textHidden
                    ? "password"
                    : "text"
                  : props.type
              }
              value={props.value ?? ""}
              onKeyUp={handleKey}
              onChange={(event) => props.onChange?.(event.target.value)}
              placeholder={props.placeholder}
              inputMode={props.inputMode}
              autoComplete={props.autoComplete}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onSubmit={props.onSubmit}
              {...(props.noPasswordManager && {
                "data-1p-ignore": true,
                "data-bwignore": true,
                "data-lpignore": true,
                "data-form-type": "other",
              })}
              aria-multiline={true}
              onKeyDown={(e) => {
                if (e.key === "Enter" && props.type !== "password") {
                  e.preventDefault();
                  props.onSubmit?.(e);
                }
              }}
            />
            {props.type === "password" && (
              <IconButtonWrapper>
                <IconButton
                  iconName={textHidden ? "EyeClosed" : "EyeOpen"}
                  onClick={() => setTextHidden(!textHidden)}
                  size={24}
                  primaryColor={theme.colors.primary}
                />
              </IconButtonWrapper>
            )}
            {props.icon && (
              <>
                <IconButtonWrapper>
                  <IconButton
                    iconName={props.icon}
                    size={24}
                    primaryColor={theme.colors.medium}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={handleIconClick}
                  />
                </IconButtonWrapper>
                <IconButtonBackground />
              </>
            )}
            {props.hasDeleteIcon && props.value && props.value.length > 0 && (
              <>
                <IconButtonWrapper>
                  <IconButton
                    iconName={"Close"}
                    size={24}
                    primaryColor={theme.colors.medium}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={handleDeleteIconClick}
                  />
                </IconButtonWrapper>
                <IconButtonBackground />
              </>
            )}
          </InputWrapper>
        )}
        {!props.withoutError && (
          <Error>
            <ErrorText>{props.error ?? " "}</ErrorText>
          </Error>
        )}
      </Container>
    );
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const IconButtonBackground = styled.div`
  position: absolute;
  right: 2px;
  top: 2px;
  background: linear-gradient(
    to left,
    ${({ theme }) => theme.colors.background},
    ${({ theme }) => theme.colors.background},
    ${({ theme }) => theme.colors.background},
    ${({ theme }) => hexWithOpacity(theme.colors.background, 0.8)},
    ${({ theme }) => hexWithOpacity(theme.colors.background, 0.6)}
  );
  width: 60px;
  height: 40px;
  z-index: 1;
  border-radius: 0px 10px 10px 0px;
`;

const IconButtonWrapper = styled.div`
  position: absolute;
  right: 0px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
`;

const ErrorText = styled(Mini)`
  color: ${({ theme }) => theme.colors.lightRed};
`;

const Error = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding-left: 5px;
  padding-right: 10px;
  max-lines: 1;
  height: 20px;
`;

const StyledInput = styled.input`
  width: 100%;
  border-radius: 10px;
  border: 1.5px solid
    ${({ theme }) => hexWithOpacity(theme.colors.medium, 0.25)};
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.dark};
  padding: 8px 12px;
  font:
    400 16px/26px Ubuntu,
    sans-serif;
  outline: none;
  box-shadow: none;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.dark};
    opacity: 0.5;
  }
  &:hover:not(:disabled):not(:focus) {
    border: 1.5px solid ${({ theme }) => theme.colors.medium};
  }
  &:focus {
    outline: none;
    border: 1.5px solid ${({ theme }) => theme.colors.primary};
  }
  &:disabled {
    opacity: 0.5;
  }
`;

const StyledMini = styled(Body)`
  color: ${({ theme }) => hexWithOpacity(theme.colors.dark, 0.6)};
  padding: 1px 0px;
`;

const StyledPlaceholder = styled(SkeletonPlaceholder)`
  width: 100%;
  height: 45px;
  border-radius: 10px;
  border: 1.5px solid
    ${({ theme }) => hexWithOpacity(theme.colors.medium, 0.25)};
`;

const LabelContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  padding-bottom: 4px;
`;

const TagValue = styled(H4)`
  background-color: ${({ theme }) => hexWithOpacity(theme.colors.medium, 0.1)};
  color: ${({ theme }) => hexWithOpacity(theme.colors.dark, 0.6)};
  border-radius: 5px;
  padding: 1px 6px;
`;
